import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FaChevronDown } from 'react-icons/fa';

const normalizeSearch = (text) =>
  String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const SCROLLBAR_BY_SCHEME = {
  blue: '[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:dark:bg-gray-700 [&::-webkit-scrollbar-thumb]:bg-blue-400 [&::-webkit-scrollbar-thumb]:dark:bg-blue-500 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-blue-500 dark:[&::-webkit-scrollbar-thumb:hover]:bg-blue-400',
  purple: '[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:dark:bg-gray-700 [&::-webkit-scrollbar-thumb]:bg-purple-400 [&::-webkit-scrollbar-thumb]:dark:bg-purple-500 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-purple-500 dark:[&::-webkit-scrollbar-thumb:hover]:bg-purple-400',
  black: '[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:dark:bg-gray-700 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:dark:bg-gray-500 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-gray-500 dark:[&::-webkit-scrollbar-thumb:hover]:bg-gray-400',
};

const ACCENT_BY_SCHEME = {
  blue: {
    triggerFocus: 'focus:ring-2 focus:ring-blue-500 focus:border-transparent',
    menuBorder: 'border-blue-400 dark:border-blue-500',
    searchBorder: 'border-blue-400 dark:border-blue-500 focus:ring-1 focus:ring-blue-500',
    selected: 'bg-blue-600 text-white',
    selectedSubtitle: 'text-blue-100',
  },
  purple: {
    triggerFocus: 'focus:ring-2 focus:ring-purple-500 focus:border-transparent',
    menuBorder: 'border-purple-400 dark:border-purple-500',
    searchBorder: 'border-purple-400 dark:border-purple-500 focus:ring-1 focus:ring-purple-500',
    selected: 'bg-purple-600 text-white',
    selectedSubtitle: 'text-purple-100',
  },
  black: {
    triggerFocus: 'focus:ring-0 focus:border-black dark:focus:border-gray-900',
    menuBorder: 'border-black dark:border-gray-900',
    searchBorder: 'border-black dark:border-gray-900 focus:border-black dark:focus:border-gray-900 focus:ring-0',
    selected: 'bg-black text-white dark:bg-gray-900',
    selectedSubtitle: 'text-gray-300',
  },
};

const CustomDropdown = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Selecione...',
  className = '',
  triggerClassName = '',
  disabled = false,
  compact = false,
  searchable = false,
  searchPlaceholder = 'Digite para buscar...',
  maxVisibleOptions = null,
  creatable = false,
  createOptionLabel = (term) => `Usar "${term}"`,
  colorScheme = 'blue',
  lightOnly = false,
  onOpenChange,
}) => {
  const accent = ACCENT_BY_SCHEME[colorScheme] || ACCENT_BY_SCHEME.blue;
  const scrollbarClasses = SCROLLBAR_BY_SCHEME[colorScheme] || SCROLLBAR_BY_SCHEME.blue;
  const safeValue = value ?? '';
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    openUpward: false,
    maxHeight: 240
  });

  const filteredOptions = useMemo(() => {
    let list;
    if (!searchable || !search.trim()) {
      list = options;
    } else {
      const term = normalizeSearch(search.trim());
      list = options.filter((opt) => normalizeSearch(opt.searchText || opt.label || '').includes(term));
    }

    if (maxVisibleOptions == null) return list;

    const pinned = list.filter((opt) => opt.value === '');
    const rest = list.filter((opt) => opt.value !== '');
    return [...pinned, ...rest.slice(0, maxVisibleOptions)];
  }, [options, search, searchable, maxVisibleOptions]);

  const menuOptions = useMemo(() => {
    if (!creatable || !search.trim()) return filteredOptions;
    const term = search.trim();
    const exists = options.some(
      (o) =>
        normalizeSearch(o.value) === normalizeSearch(term) ||
        normalizeSearch(o.label) === normalizeSearch(term)
    );
    if (exists) return filteredOptions;
    return [
      { value: `__create__:${term}`, label: createOptionLabel(term), isCreate: true },
      ...filteredOptions
    ];
  }, [creatable, search, filteredOptions, options, createOptionLabel]);

  const updateMenuPosition = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const searchOffset = searchable ? 52 : 0;
    const estimatedMenuHeight = Math.min(menuOptions.length * 44 + 8 + searchOffset, 280);
    const spaceBelow = viewportHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const openUpward = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;

    setMenuPosition({
      left: rect.left,
      width: rect.width,
      openUpward,
      top: openUpward ? rect.top - 4 : rect.bottom + 4,
      maxHeight: Math.max(140, Math.min(280, openUpward ? spaceAbove : spaceBelow))
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutsideButton = dropdownRef.current && !dropdownRef.current.contains(event.target);
      const clickedOutsideMenu = menuRef.current && !menuRef.current.contains(event.target);
      if (clickedOutsideButton && clickedOutsideMenu) {
        setIsOpen(false);
        onOpenChange?.(false);
      }
    };

    if (isOpen) {
      updateMenuPosition();
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', updateMenuPosition);
      window.addEventListener('scroll', updateMenuPosition, true);
      if (searchable) {
        setTimeout(() => searchRef.current?.focus(), 0);
      }
    } else {
      setSearch('');
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- updateMenuPosition is stable for this effect's lifecycle
  }, [isOpen, menuOptions.length, searchable]);

  const hasValue = safeValue !== '' && safeValue != null;
  const selectedOption = hasValue ? options.find((opt) => opt.value === safeValue) : null;
  const displayLabel = hasValue ? (selectedOption?.label || String(safeValue)) : '';
  const displaySubtitle = hasValue ? selectedOption?.subtitle : undefined;

  const handleSelect = (optionValue) => {
    if (String(optionValue).startsWith('__create__:')) {
      onChange(optionValue.slice('__create__:'.length) ?? '');
    } else {
      onChange(optionValue ?? '');
    }
    setIsOpen(false);
    onOpenChange?.(false);
  };

  const listMaxHeight = menuPosition.maxHeight - (searchable ? 52 : 0);

  const triggerClasses = lightOnly
    ? `
          w-full border border-gray-300 rounded-lg
          bg-white text-gray-900
          focus:outline-none ${accent.triggerFocus}
          flex items-center justify-between gap-2
          transition-colors text-left
          ${compact ? 'px-2.5 py-1.5 text-sm min-h-0' : 'px-4 py-2 min-h-[42px]'}
          ${triggerClassName}
          ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}
        `
    : `
          w-full border border-gray-300 dark:border-gray-600 rounded-lg
          bg-white dark:bg-gray-700 text-gray-900 dark:text-white
          focus:outline-none ${accent.triggerFocus}
          flex items-center justify-between gap-2
          transition-colors text-left
          ${compact ? 'px-2.5 py-1.5 text-sm min-h-0' : 'px-4 py-2 min-h-[42px]'}
          ${triggerClassName}
          ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer'}
        `;

  const labelTextClass = lightOnly
    ? (displayLabel ? 'text-gray-900' : 'text-gray-500')
    : (displayLabel ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400');

  const menuContainerClass = lightOnly
    ? `fixed z-[10000] bg-white rounded-lg shadow-lg overflow-hidden ${
        searchable ? `border ${accent.menuBorder}` : 'border border-gray-200'
      }`
    : `fixed z-[10000] bg-white dark:bg-gray-700 rounded-lg shadow-lg overflow-hidden ${
        searchable ? `border ${accent.menuBorder}` : 'border border-gray-200 dark:border-gray-600'
      }`;

  const searchWrapperClass = lightOnly
    ? 'p-2 border-b border-gray-200'
    : 'p-2 border-b border-gray-200 dark:border-gray-600';

  const searchInputClass = lightOnly
    ? `w-full px-3 py-2 text-sm rounded-lg border bg-white text-gray-900 placeholder-gray-500 focus:outline-none ${accent.searchBorder}`
    : `w-full px-3 py-2 text-sm rounded-lg border bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none ${accent.searchBorder}`;

  const emptyResultClass = lightOnly
    ? 'px-4 py-3 text-sm text-gray-500'
    : 'px-4 py-3 text-sm text-gray-500 dark:text-gray-400';

  const getOptionClass = (option) => {
    const base = `w-full text-left transition-colors ${
      compact ? 'px-2.5 py-2 text-sm' : 'px-4 py-2.5 text-[15px]'
    }`;

    if (option.isCreate) {
      return lightOnly
        ? `${base} bg-emerald-50 text-emerald-700 font-medium`
        : `${base} bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 font-medium`;
    }

    if (safeValue === option.value) {
      return `${base} ${accent.selected}`;
    }

    return lightOnly
      ? `${base} text-gray-900 hover:bg-gray-100`
      : `${base} text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600`;
  };

  const getSubtitleClass = (option) => {
    if (safeValue === option.value) {
      return accent.selectedSubtitle;
    }
    return lightOnly ? 'text-gray-500' : 'text-gray-500 dark:text-gray-400';
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          if (disabled) return;
          const nextState = !isOpen;
          setIsOpen(nextState);
          onOpenChange?.(nextState);
          if (nextState) updateMenuPosition();
        }}
        disabled={disabled}
        className={triggerClasses}
      >
        {displaySubtitle ? (
          <span className="flex-1 min-w-0 pr-1">
            <span className={`block text-sm font-medium truncate ${lightOnly ? 'text-gray-900' : 'text-gray-900 dark:text-white'}`}>{displayLabel}</span>
            <span className={`block text-xs truncate ${lightOnly ? 'text-gray-500' : 'text-gray-500 dark:text-gray-400'}`}>{displaySubtitle}</span>
          </span>
        ) : (
          <span className={`flex-1 truncate pr-2 ${labelTextClass}`}>
            {displayLabel || placeholder}
          </span>
        )}
        <FaChevronDown
          className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className={menuContainerClass}
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
              width: menuPosition.width,
              transform: menuPosition.openUpward ? 'translateY(-100%)' : 'none'
            }}
          >
            {searchable && (
              <div className={searchWrapperClass}>
                <input
                  ref={searchRef}
                  type="text"
                  value={search ?? ''}
                  onChange={(e) => setSearch(e.target.value ?? '')}
                  placeholder={searchPlaceholder}
                  className={searchInputClass}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
            )}
            <div
              className={`py-1 overflow-auto ${scrollbarClasses}`}
              style={{ maxHeight: listMaxHeight }}
            >
              {menuOptions.length === 0 ? (
                <p className={emptyResultClass}>Nenhum resultado encontrado</p>
              ) : (
                menuOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(option.value)}
                    className={getOptionClass(option)}
                  >
                    <span className="block truncate">{option.label}</span>
                    {option.subtitle && (
                      <span
                        className={`block text-xs mt-0.5 truncate ${getSubtitleClass(option)}`}
                      >
                        {option.subtitle}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CustomDropdown;
